import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SegmentedControlComponent,
  SegmentedControlItem,
  TagVariant,
} from '@catch/general-ui';
import { viewLoader } from '../../../../../../assets/animations/animations';
import { PlatformJobsTab } from '../../../../enums/platform-jobs-tab.enum';
import { PlatformJobsService } from '../../../../services/platform-jobs.service';
import { PlatformJobsInvitationsComponent } from '../../components/jobs-invitations/platform-jobs-invitations.component';
import { PlatformJobsSavedComponent } from '../../components/jobs-saved/platform-jobs-saved.component';
import { PlatformRecommendationsListComponent } from '../../../recommendations/components/recommendations-list/platform-recommendations-list.component';

@Component({
  selector: 'app-platform-jobs-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SegmentedControlComponent,
    PlatformRecommendationsListComponent,
    PlatformJobsSavedComponent,
    PlatformJobsInvitationsComponent,
  ],
  templateUrl: './platform-jobs-view.component.html',
  styleUrl: './platform-jobs-view.component.scss',
  animations: [viewLoader],
})
export class PlatformJobsViewComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly jobs = inject(PlatformJobsService);

  readonly PlatformJobsTab = PlatformJobsTab;
  readonly activeTab = signal(this.resolveInitialTab());

  readonly segmentItems = computed((): ReadonlyArray<SegmentedControlItem<PlatformJobsTab>> => [
    {
      label: 'jobboard.platform.jobs.segments.recommendations',
      value: PlatformJobsTab.RECOMMENDATIONS,
      tag: {
        label: String(this.jobs.recommendedJobs().length),
        type: TagVariant.COUNTER_DEFAULT,
      },
    },
    {
      label: 'jobboard.platform.jobs.segments.saved',
      value: PlatformJobsTab.SAVED,
      tag: {
        label: String(this.jobs.savedJobs().length),
        type: TagVariant.COUNTER_DEFAULT,
      },
    },
    {
      label: 'jobboard.platform.jobs.segments.invitations',
      value: PlatformJobsTab.INVITATIONS,
      tag: {
        label: String(this.jobs.invitations().length),
        type: TagVariant.COUNTER_DEFAULT,
      },
    },
  ]);

  /**
   * Switches the active jobs segment and syncs the URL query.
   * @param tab - Selected jobs tab
   */
  setTab(tab: PlatformJobsTab): void {
    this.activeTab.set(tab);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Picks the initial tab from the query.
   * @returns Tab to activate
   */
  private resolveInitialTab(): PlatformJobsTab {
    const requested = this.route.snapshot.queryParamMap.get('tab');
    if (
      requested === PlatformJobsTab.SAVED ||
      requested === PlatformJobsTab.INVITATIONS
    ) {
      return requested;
    }
    return PlatformJobsTab.RECOMMENDATIONS;
  }
}
